import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Target, Trophy, Settings, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const formatMoeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const MetasVendasCards = forwardRef(function MetasVendasCards({ isAdmin, carroId, carroNome }, ref) {
  const [progresso, setProgresso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configDialog, setConfigDialog] = useState(false);
  const [formConfig, setFormConfig] = useState({ meta_diaria: "", meta_semanal: "" });
  const [savingConfig, setSavingConfig] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const params = carroId ? { carro_id: carroId } : {};
      const { data } = await axios.get(`${API}/metas-vendas/progresso`, { params });
      setProgresso(data);
    } catch (err) {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [carroId]);

  useEffect(() => {
    setLoading(true);
    carregar();
    const t = setInterval(carregar, 30000);
    const onFocus = () => carregar();
    window.addEventListener("focus", onFocus);
    const onVendaCriada = () => carregar();
    window.addEventListener("venda-criada", onVendaCriada);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("venda-criada", onVendaCriada);
    };
  }, [carregar]);

  useImperativeHandle(ref, () => ({ refresh: carregar }), [carregar]);

  const abrirConfig = async () => {
    try {
      const params = carroId ? { carro_id: carroId } : {};
      const { data } = await axios.get(`${API}/metas-vendas/config`, { params });
      setFormConfig({
        meta_diaria: String(data.meta_diaria || 3000),
        meta_semanal: String(data.meta_semanal || 18000),
      });
      setConfigDialog(true);
    } catch (err) {
      toast.error("Erro ao carregar configuração");
    }
  };

  const salvarConfig = async (e) => {
    e?.preventDefault?.();
    const md = parseFloat((formConfig.meta_diaria || "").replace(",", "."));
    const ms = parseFloat((formConfig.meta_semanal || "").replace(",", "."));
    if (!(md > 0) || !(ms > 0)) {
      toast.error("Informe valores válidos");
      return;
    }
    setSavingConfig(true);
    try {
      await axios.put(`${API}/metas-vendas/config`, {
        meta_diaria: md,
        meta_semanal: ms,
        carro_id: carroId || null,
      });
      toast.success(`Metas do ${carroNome || 'carro'} atualizadas!`);
      setConfigDialog(false);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading || !progresso) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className="h-24 bg-gradient-to-br from-emerald-100 to-emerald-50 animate-pulse rounded-xl border border-emerald-200" />
        <div className="h-24 bg-gradient-to-br from-blue-100 to-blue-50 animate-pulse rounded-xl border border-blue-200" />
      </div>
    );
  }

  const {
    e_sabado, meta_diaria, meta_semanal,
    vendido_hoje, vendido_semana,
    pct_diaria, pct_semanal,
    meta_diaria_atingida, meta_semanal_atingida,
    card_sabado,
  } = progresso;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-testid="metas-vendas-painel">
        {/* === CARD ESQUERDO: Meta Diária OU Sábado (HIPERMETA / META RESTANTE) === */}
        {e_sabado && card_sabado?.tipo === "hipermeta" ? (
          <Card className="overflow-hidden border-2 border-amber-400 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 shadow-lg relative" data-testid="card-hipermeta">
            {/* Sparkles decorativos */}
            <div className="absolute top-2 right-2 text-amber-500 animate-pulse">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="absolute bottom-2 left-2 text-orange-400 animate-pulse" style={{ animationDelay: "0.5s" }}>
              <Sparkles className="h-4 w-4" />
            </div>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-md">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-orange-700 tracking-wide flex items-center gap-1">
                    🎉 HIPERMETA <Sparkles className="h-3 w-3" />
                  </p>
                  <p className="text-[10px] text-amber-700">Você superou a meta semanal!</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-amber-700 font-semibold">Vendido acima da meta</p>
                <p className="text-2xl font-extrabold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                  +{formatMoeda(card_sabado.valor_acima)}
                </p>
                <p className="text-[10px] text-amber-700">Total da semana: {formatMoeda(vendido_semana)} / Meta {formatMoeda(meta_semanal)}</p>
              </div>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={abrirConfig} className="absolute top-1 right-1 h-7 w-7 p-0 text-amber-700 hover:bg-amber-100" data-testid="btn-config-metas">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              )}
            </CardContent>
          </Card>
        ) : e_sabado && card_sabado?.tipo === "meta_restante" ? (
          <Card className="overflow-hidden border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50" data-testid="card-meta-restante">
            <CardContent className="p-4 space-y-2 relative">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-orange-500 text-white flex items-center justify-center shadow">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-red-700 tracking-wide">META RESTANTE</p>
                  <p className="text-[10px] text-red-700">Falta para bater a semana</p>
                </div>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-red-700">{formatMoeda(card_sabado.valor_restante)}</p>
                <p className="text-[10px] text-red-700">Semana: {formatMoeda(vendido_semana)} / {formatMoeda(meta_semanal)}</p>
              </div>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={abrirConfig} className="absolute top-1 right-1 h-7 w-7 p-0 text-red-700 hover:bg-red-100" data-testid="btn-config-metas">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Meta Diária padrão (seg-sex) */
          <Card className={`overflow-hidden border-2 ${meta_diaria_atingida ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-green-50" : "border-emerald-200 bg-gradient-to-br from-white to-emerald-50"}`} data-testid="card-meta-diaria">
            <CardContent className="p-4 space-y-2 relative">
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white shadow ${meta_diaria_atingida ? "bg-gradient-to-br from-emerald-500 to-green-600" : "bg-gradient-to-br from-emerald-400 to-green-500"}`}>
                  <Target className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-emerald-700 tracking-wide">META DIÁRIA</p>
                  <p className="text-[10px] text-gray-500">Meta de hoje: {formatMoeda(meta_diaria)}</p>
                </div>
              </div>
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <p className="text-xl font-extrabold text-emerald-700" data-testid="meta-diaria-vendido">{formatMoeda(vendido_hoje)}</p>
                  <p className="text-[10px] font-bold text-gray-500">{pct_diaria.toFixed(0)}%</p>
                </div>
                <div className="w-full bg-emerald-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-2.5 rounded-full transition-all duration-700 ${meta_diaria_atingida ? "bg-gradient-to-r from-emerald-500 to-green-600" : "bg-gradient-to-r from-emerald-400 to-green-500"}`}
                    style={{ width: `${Math.min(pct_diaria, 100)}%` }}
                    data-testid="meta-diaria-bar"
                  />
                </div>
                {meta_diaria_atingida && (
                  <p className="text-[10px] font-bold text-emerald-700 mt-1 flex items-center gap-1">✅ Meta diária batida!</p>
                )}
              </div>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={abrirConfig} className="absolute top-1 right-1 h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-100" data-testid="btn-config-metas">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* === CARD DIREITO: Meta Semanal === */}
        <Card className={`overflow-hidden border-2 ${meta_semanal_atingida ? "border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50" : "border-blue-200 bg-gradient-to-br from-white to-blue-50"}`} data-testid="card-meta-semanal">
          <CardContent className="p-4 space-y-2 relative">
            <div className="flex items-center gap-2">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white shadow ${meta_semanal_atingida ? "bg-gradient-to-br from-blue-500 to-indigo-600" : "bg-gradient-to-br from-blue-400 to-indigo-500"}`}>
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase text-blue-700 tracking-wide">META SEMANAL</p>
                <p className="text-[10px] text-gray-500">Meta da semana: {formatMoeda(meta_semanal)}</p>
              </div>
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-xl font-extrabold text-blue-700" data-testid="meta-semanal-vendido">{formatMoeda(vendido_semana)}</p>
                <p className="text-[10px] font-bold text-gray-500">{pct_semanal.toFixed(0)}%</p>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-2.5 rounded-full transition-all duration-700 ${meta_semanal_atingida ? "bg-gradient-to-r from-blue-500 to-indigo-600" : "bg-gradient-to-r from-blue-400 to-indigo-500"}`}
                  style={{ width: `${Math.min(pct_semanal, 100)}%` }}
                  data-testid="meta-semanal-bar"
                />
              </div>
              {meta_semanal_atingida && (
                <p className="text-[10px] font-bold text-blue-700 mt-1 flex items-center gap-1">🏆 Meta semanal batida!</p>
              )}
            </div>
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={abrirConfig} className="absolute top-1 right-1 h-7 w-7 p-0 text-blue-700 hover:bg-blue-100" data-testid="btn-config-metas-semanal">
                <Settings className="h-3.5 w-3.5" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Configurar Metas */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-md" data-testid="dialog-config-metas">
          <DialogHeader>
            <DialogTitle>Configurar Metas {carroNome ? `· ${carroNome}` : ""}</DialogTitle>
          </DialogHeader>
          <form onSubmit={salvarConfig} className="space-y-4">
            <div>
              <Label htmlFor="meta-d" className="text-sm font-semibold">Meta Diária (R$)</Label>
              <Input
                id="meta-d"
                type="number"
                step="0.01"
                min="0"
                value={formConfig.meta_diaria}
                onChange={(e) => setFormConfig({ ...formConfig, meta_diaria: e.target.value })}
                placeholder="3000.00"
                data-testid="input-meta-diaria"
                required
              />
              <p className="text-[11px] text-gray-500 mt-1">Aplicada de segunda a sexta-feira.</p>
            </div>
            <div>
              <Label htmlFor="meta-s" className="text-sm font-semibold">Meta Semanal (R$)</Label>
              <Input
                id="meta-s"
                type="number"
                step="0.01"
                min="0"
                value={formConfig.meta_semanal}
                onChange={(e) => setFormConfig({ ...formConfig, meta_semanal: e.target.value })}
                placeholder="18000.00"
                data-testid="input-meta-semanal"
                required
              />
              <p className="text-[11px] text-gray-500 mt-1">No sábado, o card vira "META RESTANTE" ou "HIPERMETA".</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConfigDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={savingConfig} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-salvar-metas">
                {savingConfig ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default MetasVendasCards;
